import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, UserPlus, Trash2, Shield, Users } from "lucide-react";

const ROLE_LABEL = { admin: "Administrator", salesperson: "Salesperson / PM", office: "Office / Estimator" };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "salesperson" });
  const [saving, setSaving] = useState(false);

  const load = async () => { try { const { data } = await api.get("/users"); setUsers(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!f.name || !f.email || !f.password) { toast.error("Fill all fields"); return; }
    setSaving(true);
    try { await api.post("/users", f); toast.success("User created"); setOpen(false); setF({ name: "", email: "", password: "", role: "salesperson" }); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const del = async (id) => { try { await api.delete(`/users/${id}`); toast.success("Deleted"); await load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <AppShell title="Administration">
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2"><Users className="w-6 h-6 text-orange-400" /><h1 className="font-display text-3xl font-extrabold">Users & Roles</h1></div>
          <Button data-testid="add-user-button" onClick={() => setOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-slate-950 font-bold"><UserPlus className="w-4 h-4 mr-2" />Add User</Button>
        </div>

        {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-orange-500 animate-spin" /></div> : (
          <div className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden">
            {users.map((u) => (
              <div key={u.id} data-testid={`user-row-${u.email}`} className="flex items-center justify-between px-5 py-4 border-b border-slate-800 last:border-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">{u.role === "admin" ? <Shield className="w-5 h-5 text-orange-400" /> : <Users className="w-5 h-5 text-slate-400" />}</div>
                  <div><div className="font-semibold">{u.name}</div><div className="text-sm text-slate-500">{u.email}</div></div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-slate-800 text-slate-300">{ROLE_LABEL[u.role]}</span>
                  {u.role !== "admin" && <Button size="icon" variant="ghost" data-testid={`delete-user-${u.email}`} onClick={() => del(u.id)} className="text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader><DialogTitle className="font-display">Add User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-slate-400 text-xs">Name</Label><Input data-testid="user-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-slate-950 border-slate-700 mt-1" /></div>
            <div><Label className="text-slate-400 text-xs">Email</Label><Input data-testid="user-email-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-slate-950 border-slate-700 mt-1" /></div>
            <div><Label className="text-slate-400 text-xs">Password</Label><Input data-testid="user-password-input" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="bg-slate-950 border-slate-700 mt-1" /></div>
            <div><Label className="text-slate-400 text-xs">Role</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger data-testid="user-role-select" className="bg-slate-950 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100"><SelectItem value="salesperson">Salesperson / PM</SelectItem><SelectItem value="office">Office / Estimator</SelectItem><SelectItem value="admin">Administrator</SelectItem></SelectContent></Select></div>
          </div>
          <DialogFooter><Button data-testid="user-save-button" onClick={create} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-slate-950">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
